<template>
  <div>
    <NuxtLayout
      name="asuostaticlayout"
      :title="$page?.financialAid.title"
      :sub-title="$page?.financialAid.heroDescription"
      :image-source="$page?.financialAid.heroImage"
      staticRfiBgColor="#ffffff"
      :is-header-sticky="false"
      :is-sticky-brand-nav="false"
    >
      <template #hero>
        <div
          class="container-fluid hero-image p-0 position-relative"
          :style="headerImageStyle"
        >
          <div class="hero-image-gradient w-100 position-absolute"></div>
          <!-- title -->
          <div class="position-absolute shr-title pe-space-xs text-light-1">
            <h1 class="h3-small mb-space-xs">
              {{ $page?.financialAid.titleLabel }}
            </h1>
            <h2 class="h1-large text-white">
              {{ $page?.financialAid.titleStart }}
              <span class="p-space-xxs bg-secondary text-dark-3">{{
                $page?.financialAid.titleHighlight
              }}</span>
            </h2>
            <div
              class="mb-0 mt-space-sm d-none d-lg-block ab-subtitle w-75 deep-p-mb-0"
              v-html="$page?.financialAid.heroDescription"
            ></div>
          </div>
        </div>
      </template>
      <!-- section financial-aid-info -->
      <FinancialAidInfo
        v-if="$page?.financialAid"
        :title="$page?.financialAid.infoTitle"
        :description="$page?.financialAid.infoDescription"
        :footnote="$page?.financialAid.infoFootnote"
        :tabs="$page.financialAid.infoTabs"
      ></FinancialAidInfo>
      <!-- section stats -->
      <section
        class="py-space-md py-lg-space-xxl bg-light-1"
        v-if="$page?.financialAid.statsTitleStart"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 col-xxl-10">
              <h3 class="h3-large mb-space-sm">
                {{ $page.financialAid.statsTitleLabel }}
              </h3>
              <h2 class="h1-xl mb-space-sm">
                <span>{{ $page.financialAid.statsTitleStart + " " }}</span>
                <span class="bg-success text-dark-3 px-space-xxs">{{
                  $page.financialAid.statsTitleHighlighted
                }}</span>
                <br />
                <span>{{ " " + $page.financialAid.statsTitleEnd }}</span>
              </h2>
              <div
                v-html="$page.financialAid.statsDescription"
                class="mb-space-md stats-desc deep-p-mb-0"
              ></div>
              <div
                v-html="$page.financialAid.statsFootnote"
                class="fw-bold mb-0 deep-p-mb-0"
              ></div>
            </div>
          </div>
        </div>
      </section>
      <!-- section fafsa info -->
      <section
        class="py-space-md pt-lg-space-xxl pb-lg-space-lg"
        id="fafsa-info"
      >
        <div class="container">
          <div class="row mb-space-sm mb-lg-space-lg">
            <div class="col-12 col-lg-8 offset-lg-2 text-center px-lg-space-xl">
              <h2 class="h2-large mb-space-xs">
                {{ $page?.financialAid.fafsaTitle }}
              </h2>
              <div
                class="fafsa-desc mb-space-md"
                v-html="$page?.financialAid.fafsaDescription"
              ></div>
              <a
                class="btn btn-secondary"
                :href="$page?.financialAid.fafsaLink.uri"
                :target="$page?.financialAid.fafsaLink.target"
                @click="handleFafsaLinkClick"
                >{{ $page?.financialAid.fafsaLink.text }}</a
              >
            </div>
          </div>
          <div class="row">
            <div
              class="col-12 col-lg-3 px-space-lg px-lg-space-xs mb-space-sm mb-lg-0"
              v-for="(card, idx) in $page?.financialAid.fafsaCards"
              :key="idx"
            >
              <div class="px-space-sm py-space-md bg-light-1 border h-100">
                <div class="bg-light">
                  <div
                    class="d-flex justify-content-center align-items-center bg-secondary rounded-circle mb-space-xs fafsa-icon"
                  >
                    <img :src="card.icon!" :alt="card.iconAlt!" />
                  </div>
                  <h3 class="h3-medium mb-space-xs">{{ card.title }}</h3>
                  <div class="deep-p-mb-0" v-html="card.text"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <!-- fafsa calendar -->
      <section
        class="position-relative pb-space-md pb-lg-space-xxl"
        ref="fafsaCalendar"
        @touchmove="tooltipClose"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 col-lg-4 offset-lg-1">
              <div
                class="sticky-lg-top pt-space-xxs pt-lg-space-lg mb-space-lg"
              >
                <h2 class="h2-medium mb-space-sm">
                  {{ $page?.financialAid.infographicsTitle }}
                </h2>
                <div v-html="$page?.financialAid.infographicsDescription"></div>
                <div class="text-primary d-flex fs-small">
                  <span>{{ $page?.financialAid.infographicsTooltipText }}</span>
                  <ToolTip :tooltip-width="420" ref="tooltipComp">
                    <template #tooltip>
                      <div
                        class="text-dark-3"
                        v-html="
                          $page?.financialAid.infographicsTooltipDescription
                        "
                      ></div>
                    </template>
                    <font-awesome-icon
                      class="info-icon text-primary"
                      icon="info-circle"
                    ></font-awesome-icon>
                  </ToolTip>
                </div>
              </div>
            </div>
            <div
              class="col-12 col-lg-4 offset-lg-2 pt-space-xxs pt-lg-space-lg"
            >
              <img
                class="w-100"
                :src="$page?.financialAid.infographicsImage"
                :alt="$page?.financialAid.infographicsImageAlt"
              />
            </div>
          </div>
        </div>
      </section>
      <!-- section video -->
      <SectionVideoApollo
        :youtube-id="$page?.financialAid.video.youtubeId!"
        :title="$page?.financialAid.video.title!"
        title-size="large"
        :label="$page?.financialAid.video.description!"
        label-size="large"
        label-variant="light-1"
        :bg-image-source="$page?.financialAid.video.imageSource!"
        :applyMdBlockClass="false"
        modal-text-variant="white"
        @modal-open="handleVideoModalOpen"
        @modal-close="handleVideoModalClose"
      ></SectionVideoApollo>
      <!-- section connect -->
      <section class="py-space-lg py-lg-space-xxl" v-if="$page?.financialAid">
        <div class="container-fluid">
          <div class="row">
            <div
              class="col-12 col-lg-4 offset-lg-1 px-space-xs order-2 order-lg-1 pt-space-sm pt-lg-space-0"
            >
              <h2 class="h2-medium mb-space-sm connect-header">
                {{ $page?.financialAid.connectTitle }}
              </h2>
              <div
                v-for="(card, idx) in $page?.financialAid.connectCards"
                :key="idx"
                :class="[
                  $page?.financialAid.connectCards.length - 1 === idx
                    ? 'border-bottom-0'
                    : 'border-bottom',
                  idx === 0 ? 'pb-space-xs' : 'py-space-xs',
                  'pe-lg-space-lg',
                ]"
              >
                <h3 class="h3-large mb-space-xxs">
                  {{ card.title }}
                </h3>
                <div
                  v-html="card.description"
                  class="text-dark-1 fs-small deep-p-mb-0 mb-space-xxs"
                ></div>
                <a
                  class="rds-link"
                  :href="card.link.uri"
                  :target="card.link.target"
                  @click.prevent="
                    handleConnectClick(card.title, card.link, idx === 1)
                  "
                  >{{ card.link.text }}</a
                >
              </div>
            </div>
            <div class="col-12 col-lg-7 px-0 ps-lg-space-xs order-1 order-lg-2">
              <div
                class="section-bg h-100 w-100 rds-lazy"
                :data-background-image="$page?.financialAid.connectImage"
              ></div>
            </div>
          </div>
        </div>
      </section>
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { SectionVideoApollo } from "@rds-vue-ui/section-video-apollo";
import ToolTip from "~/components/ToolTip.vue";
import FinancialAidInfo from "~/components/what-it-costs-page/FinancialAidInfo.vue";
import {
  FinancialAid,
  ParagraphHtmlLink,
} from "~/server/resolvers/resolvers-types";

interface IPage {
  financialAid: FinancialAid;
}
const siteUrl = useRuntimeConfig().public.siteUrl;
const schemaService = useSchemaService();
/*
********************************************
Page quries
********************************************
*/
const $page = await usePageQuery<IPage>(
  `#graphql
  query FinancialAid($financialAidId: ID!) {
    financialAid(id: $financialAidId) {
      detailPage
      title
      titleStart
      titleHighlight
      titleLabel
      heroDescription
      heroImage
      heroImageAlt
      slug
      metaTitle
      metaDescription
      infoTitle
      infoDescription
      infoFootnote
      infoTabs {
        title
        accordions {
          title
          shortDescription
          description
        }
      }
      statsTitleStart
      statsTitleHighlighted
      statsTitleEnd
      statsTitleLabel
      statsDescription
      statsFootnote
      fafsaTitle
      fafsaDescription
      fafsaLink {
        text
        uri
        target
      }
      fafsaCards {
        title
        text
        icon
        iconAlt
      }
      infographicsTitle
      infographicsDescription
      infographicsTooltipText
      infographicsTooltipDescription
      infographicsImage
      infographicsImageAlt
      video {
        title
        description
        youtubeId
        imageSource
        imageAlt
        uploadDate
        duration
      }
      connectTitle
      connectImage
      connectImageAlt
      connectCards {
        title
        description
        link {
          text
          uri
          target
        }
      }
    }
  }
`,
  {
    financialAidId: "5c364f6f-6761-40dc-b733-a0e003754c7b",
  },
  {
    key: "financial-aid-page",
  }
);

/*
********************************************
Page meta tags
********************************************
*/
interface IPageOffers {
  "@type": string;
  name: string;
  description: string;
  url: string;
}
const generatePageSchema = () => {
  const pageUrl = useUtilityService().formatCanonicalUrl(
    `${siteUrl}${$page?.financialAid.detailPage}`
  );
  const pageName = $page?.financialAid.metaTitle?.split("|")?.[0].trim();

  const pageOffers: IPageOffers[] = [];

  // contact items
  if ($page?.financialAid.connectCards) {
    $page?.financialAid.connectCards.forEach((item) => {
      pageOffers.push({
        "@type": "WebPage",
        name: item.title,
        description: item.description,
        url: item?.link?.uri!,
      });
    });
  }

  return {
    "@context": "http://schema.org",
    "@type": "Webpage",
    name: pageName,
    description: $page?.financialAid.metaDescription,
    url: pageUrl,
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "ASU Online",
          item: "https://asuonline.asu.edu/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Tuition",
          item: "https://asuonline.asu.edu/what-it-costs/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: pageName,
          item: pageUrl,
        },
      ],
    },
    mainEntityOfPage: {
      "@type": "Webpage",
      "@id": pageUrl,
    },
    offers: {
      "@type": "Offer",
      itemOffered: pageOffers,
    },
    provider: useSchemaService().getOrganizationSchema(),
  };
};

definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"],
  },
});
useHead({
  title: $page?.financialAid.metaTitle,
  meta: [
    {
      name: "Title",
      content: $page?.financialAid.metaTitle,
    },
    {
      name: "Description",
      content: $page?.financialAid.metaDescription,
    },
    ...useUtilityService().generateSocialTags(
      $page?.financialAid.metaTitle as string,
      $page?.financialAid.metaDescription as string,
      $page?.financialAid.heroImage as string
    ),
  ],
  link: [
    {
      rel: "canonical",
      href: useUtilityService().formatCanonicalUrl(
        `${siteUrl}${$page?.financialAid.detailPage}`
      ),
    },
  ],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(generatePageSchema()),
    },
  ],
});

const fafsaCalendar = ref<HTMLElement>();
const tooltipComp = ref<InstanceType<typeof ToolTip>>();

const handleFafsaLinkClick = (e: Event) => {
  e.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    $page!.financialAid.fafsaTitle.toLowerCase(),
    $page!.financialAid.fafsaLink.text.toLowerCase()
  );
  navigateTo($page?.financialAid.fafsaLink.uri, {
    open: {
      target: $page!.financialAid.fafsaLink.target,
    },
  });
};
const tooltipClose = () => {
  tooltipComp.value?.hideTooltip();
};
const handleVideoModalOpen = () => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "open",
    "click",
    "main content",
    $page!.financialAid.video.title!.toLowerCase(),
    "watch video"
  );
};
const handleVideoModalClose = () => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "close",
    "click",
    "main content",
    $page!.financialAid.video.title!.toLowerCase(),
    "close cross"
  );
};
const handleConnectClick = (
  title: string,
  link: ParagraphHtmlLink,
  internal: boolean
) => {
  const { text, uri, target } = link;
  let linkType = internal ? "internal link" : "external link";
  analyticsComposable.trackLinkEvent(
    linkType,
    "main content",
    title.toLowerCase(),
    text.toLowerCase(),
    $page?.financialAid.connectTitle.toLowerCase()
  );
  navigateTo(uri, {
    open: {
      target: target,
    },
  });
};

const headerImageStyle = computed(() => {
  return { "background-image": `url('${$page?.financialAid.heroImage}')` };
});
</script>

<style lang="scss" scoped>
.fa-info-section {
  :deep(a) {
    color: var(--rds-secondary);
    &:focus {
      outline: 2px solid var(--rds-light-1);
    }
  }
}

.contact-card-icon-height :deep(img) {
  height: 54px !important;
}

.fa-info-section-hero-img {
  width: 100%;
}

.fa-parallax {
  background: var(--rds-dark-3, #191919) !important;
}
.hero-image {
  min-height: 256px;
  background-size: cover;
  background-position: top;
  background-repeat: no-repeat;
}

.hero-image-gradient {
  height: 100%;
  bottom: 0;
  background: transparent linear-gradient(180deg, #19191900 0%, #191919 100%) 0%
    0% no-repeat padding-box;
  opacity: 1;
}

.shr-title {
  bottom: 32px;
  left: 24px;
}
.deep-p-mb-0 :deep(p) {
  margin-bottom: 0;
}
.info-switch :deep(.active-tab-button) {
  color: var(--rds-dark-3) !important;
}
.info-switch {
  & :deep(.tab-list) {
    padding: 4px !important;
    gap: 4px !important;
  }
  & :deep(.tab-list .highlight) {
    top: 4px !important;
    bottom: 4px !important;
  }
}

.fafsa-desc :deep(strong) {
  font-size: 12px;
  color: var(--rds-primary);
}
.fafsa-icon {
  height: 32px;
  width: 32px;
  & img {
    height: 16px;
    width: 16px;
    aspect-ratio: 1 / 1;
  }
}
.section-bg {
  background-size: cover;
  background-position: top;
  background-repeat: no-repeat;
  min-height: 256px;
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .fa-info-section {
    background: var(--rds-dark-3);
    background: linear-gradient(
      var(--rds-dark-3) 0%,
      var(--rds-dark-3) 25%,
      var(--rds-dark-3) 25%,
      var(--rds-dark-3) 100%
    );
  }
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .fa-info-section {
    background: var(--rds-dark-3);
    background: linear-gradient(
      var(--rds-white) 0%,
      var(--rds-white) 25%,
      var(--rds-dark-3) 25%,
      var(--rds-dark-3) 100%
    );
  }
  .fa-parallax {
    background: inherit !important;
  }
  .hero-image {
    min-height: 600px;
  }
  .stats-desc {
    width: 75%;
  }
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .shr-title {
    left: 48px;
    bottom: 32px;
  }
}

/* Large devices (desktops, 992px to 1199px) */
@media (min-width: 992px) and (max-width: 1199px) {
  .shr-title {
    left: calc((100vw - 960px) / 2);
    bottom: 48px;
  }
}
/* X-Large devices (1200px and up) */
@media (min-width: 1200px) {
  .shr-title {
    left: calc((100vw - 1140px) / 2);
    bottom: 48px;
  }
  .connect-header {
    width: 80%;
  }
}

/* XX-Large devices (1400px and up) */
@media (min-width: 1400px) {
  .shr-title {
    left: calc((100vw - 1300px) / 2);
  }
}
</style>
